import { useState } from 'react';
import { Erfolgsmeldung } from '../../../gemeinsam/komponenten/Meldungen';
import { Link } from 'react-router-dom';
import { Laenderprofil } from '../komponenten/Laenderprofil';
import { Zweifaktor } from '../../anmeldung/komponenten/Zweifaktor';
import Geraete from '../../anmeldung/komponenten/Geraete';
import { Privatsphaere } from '../komponenten/Privatsphaere';
import { Erinnerungen } from '../../aufgaben/komponenten/Erinnerungen';
import { Wiederherstellung } from '../../mandanten/komponenten/Wiederherstellung';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { useDaten, datum } from '../../../gemeinsam/lib/daten';
import { useAuth } from '../../../gemeinsam/lib/AuthContext';
import { Feld, Hinweis, Karte, KartenKopf, Knopf } from '../../../gemeinsam/komponenten/Basis';

type Sepa = {
  id: string; holder: string; ibanMasked: string; bic: string | null;
  signedAt: string; mandatsreferenz: string | null;
} | null;

type Vorschau = {
  firmenname: string;
  stand: Record<string, number>;
  aufbewahrungspflichtig: boolean;
  warnung: string | null;
  hinweis: string;
  sicherung?: { tage: number; text: string };
};

const BEREICHE = [
  { id: 'stammdaten',  name: 'Kunden, Lieferanten, Artikel' },
  { id: 'belege',      name: 'Rechnungen, Retouren, Guthaben' },
  { id: 'buchhaltung', name: 'Ausgaben' },
  { id: 'aufgaben',    name: 'Aufgaben' },
  { id: 'chat',        name: 'Nachrichten' },
  { id: 'fahrtenbuch', name: 'Fahrzeuge und Fahrten' },
];

const NAMEN: Record<string, string> = {
  kunden: 'Kunden', lieferanten: 'Lieferanten', artikel: 'Artikel',
  rechnungen: 'Rechnungen', gestellteBelege: 'davon gestellt', ausgaben: 'Ausgaben',
  retouren: 'Retouren', aufgaben: 'Aufgaben', fahrten: 'Fahrten',
  protokollEintraege: 'Protokolleinträge',
};

/** Was nur den Betrieb betrifft — sieht und lädt nur ein Administrator. */
function Betrieb() {
  const { mandant, istAdmin, profil } = useAuth();
  const { daten: sepa, neuLaden: sepaNeu } = useDaten<Sepa>('/mandant/sepa');
  const { daten: vorschau, neuLaden: vorschauNeu } = useDaten<Vorschau>('/mandant/zuruecksetzen/vorschau');

  const [sepaFormular, setSepaFormular] = useState<{ holder: string; iban: string; bic: string; zustimmung: boolean } | null>(null);
  const [resetOffen, setResetOffen] = useState(false);
  const [bestaetigung, setBestaetigung] = useState('');
  const [gewaehlt, setGewaehlt] = useState<string[]>(BEREICHE.map(b => b.id));
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);

  async function sepaSpeichern(e: React.FormEvent) {
    e.preventDefault();
    if (!sepaFormular) return;
    setSendet(true);
    setFehler(null);
    try {
      await api('/mandant/sepa', { methode: 'POST', daten: sepaFormular });
      setSepaFormular(null);
      setMeldung('SEPA-Lastschriftmandat erteilt.');
      await sepaNeu();
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Mandat konnte nicht erteilt werden.');
    } finally { setSendet(false); }
  }

  async function zuruecksetzen(e: React.FormEvent) {
    e.preventDefault();
    setSendet(true);
    setFehler(null);
    try {
      const r = await api<{ vorher: Record<string, number> }>('/mandant/zuruecksetzen', {
        methode: 'POST', daten: { bestaetigung, bereiche: gewaehlt },
      });
      setResetOffen(false);
      setBestaetigung('');
      const summe = Object.entries(r.vorher)
        .filter(([k]) => k !== 'protokollEintraege' && k !== 'gestellteBelege')
        .reduce((s, [, v]) => s + v, 0);
      setMeldung(`${summe} Datensätze wurden entfernt. Das Änderungsprotokoll bleibt als Nachweis erhalten.`);
      await vorschauNeu();
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Zurücksetzen fehlgeschlagen.');
    } finally { setSendet(false); }
  }

  return (
    <div className="space-y-4">
      {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
      <Erfolgsmeldung text={meldung} />

      <Erinnerungen />

      {istAdmin && profil && <Laenderprofil profil={profil} aktualisiert={() => {}} />}

      {istAdmin && (
        <Karte>
          <KartenKopf
            titel="SEPA-Lastschriftmandat"
            hinweis="Damit wird der Monatsbeitrag von Ihrem Konto eingezogen"
            aktion={!sepaFormular && (
              <Knopf variante={sepa ? 'sekundaer' : 'primaer'}
                     onClick={() => { setSepaFormular({ holder: mandant?.name ?? '', iban: '', bic: '', zustimmung: false }); setMeldung(null); }}>
                {sepa ? 'Bankverbindung ändern' : 'Mandat erteilen'}
              </Knopf>
            )}
          />

          {sepaFormular ? (
            <form onSubmit={sepaSpeichern} className="space-y-4 p-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <Feld label="Kontoinhaber" value={sepaFormular.holder} required autoFocus
                      onChange={e => setSepaFormular(f => f && ({ ...f, holder: e.target.value }))} />
                <Feld label="IBAN" value={sepaFormular.iban} required
                      placeholder="DE00 0000 0000 0000 0000 00"
                      hinweis="Die Prüfziffer wird geprüft — Zahlendreher fallen sofort auf."
                      onChange={e => setSepaFormular(f => f && ({ ...f, iban: e.target.value }))} />
                <Feld label="BIC (optional)" value={sepaFormular.bic}
                      onChange={e => setSepaFormular(f => f && ({ ...f, bic: e.target.value }))} />
              </div>

              <label className="flex items-start gap-2.5 rounded-lg border border-slate-200 p-3 text-sm dark:border-slate-700">
                <input type="checkbox" className="mt-0.5" checked={sepaFormular.zustimmung}
                       onChange={e => setSepaFormular(f => f && ({ ...f, zustimmung: e.target.checked }))} />
                <span className="text-slate-700 dark:text-slate-300">
                  Ich ermächtige LagerPro, Zahlungen von meinem Konto mittels Lastschrift einzuziehen.
                  Zugleich weise ich mein Kreditinstitut an, die von LagerPro auf mein Konto gezogenen
                  Lastschriften einzulösen. Ich kann innerhalb von acht Wochen ab Belastungsdatum
                  die Erstattung verlangen.
                </span>
              </label>

              <div className="flex gap-2">
                <Knopf type="submit" variante="primaer" laedt={sendet} disabled={!sepaFormular.zustimmung}>
                  Mandat erteilen
                </Knopf>
                <Knopf type="button" onClick={() => { setSepaFormular(null); setFehler(null); }}>Abbrechen</Knopf>
              </div>
            </form>
          ) : sepa ? (
            <dl className="divide-y divide-slate-200 text-sm dark:divide-slate-800">
              {[
                ['Kontoinhaber', sepa.holder],
                ['IBAN', sepa.ibanMasked],
                ['BIC', sepa.bic || '—'],
                ['Mandatsreferenz', sepa.mandatsreferenz || '—'],
                ['Erteilt am', datum(sepa.signedAt)],
              ].map(([t, w]) => (
                <div key={t} className="flex justify-between px-5 py-2.5">
                  <dt className="text-slate-500 dark:text-slate-400">{t}</dt>
                  <dd className="font-mono text-slate-900 dark:text-slate-100">{w}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <div className="p-5">
              <Hinweis art="warnung">
                Es ist kein Lastschriftmandat hinterlegt. Ohne Mandat kann der Monatsbeitrag nicht
                eingezogen werden.
              </Hinweis>
            </div>
          )}
        </Karte>
      )}

      {istAdmin && vorschau && (
        <Karte klasse="border-rose-300 dark:border-rose-900">
          <KartenKopf titel="Daten zurücksetzen"
                      hinweis="Entfernt Ihre Bewegungsdaten — Benutzer, Firmenangaben und Tarif bleiben" />
          <div className="space-y-4 p-5">
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-3">
              {Object.entries(vorschau.stand)
                .filter(([, v]) => v > 0)
                .map(([k, v]) => (
                  <div key={k} className="flex justify-between">
                    <span className="text-slate-500 dark:text-slate-400">{NAMEN[k] ?? k}</span>
                    <span className="font-medium text-slate-900 dark:text-slate-100">{v}</span>
                  </div>
                ))}
            </div>

            {/* Der Kunde muss vor dem Klick wissen, dass eine Sicherung entsteht.
                Eine stille Kopie nach einem Löschbefehl wäre ein Vertrauensbruch. */}
            {vorschau.sicherung && (
              <Hinweis art="info" titel="Eine Sicherung wird angelegt">
                {vorschau.sicherung.text}
              </Hinweis>
            )}

            {vorschau.warnung && (
              <Hinweis art="fehler" titel="Aufbewahrungspflicht beachten">
                {vorschau.warnung}
                <span className="mt-2 block">
                  <Link to="/gobd" className="font-medium underline">GDPdU-Export sichern</Link>
                  {' · '}
                  <Link to="/buchhaltung" className="font-medium underline">DATEV-Stapel sichern</Link>
                </span>
              </Hinweis>
            )}

            {!resetOffen ? (
              <Knopf variante="gefahr" onClick={() => { setResetOffen(true); setMeldung(null); }}>
                Zurücksetzen vorbereiten
              </Knopf>
            ) : (
              <form onSubmit={zuruecksetzen} className="space-y-4 rounded-lg border border-rose-300 bg-rose-50/50 p-4 dark:border-rose-900 dark:bg-rose-950/20">
                <div>
                  <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">Was soll entfernt werden?</p>
                  <div className="space-y-1.5">
                    {BEREICHE.map(b => (
                      <label key={b.id} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                        <input
                          type="checkbox"
                          checked={gewaehlt.includes(b.id)}
                          onChange={e => setGewaehlt(g => e.target.checked ? [...g, b.id] : g.filter(x => x !== b.id))}
                        />
                        {b.name}
                      </label>
                    ))}
                  </div>
                </div>

                <Feld
                  label={`Zum Bestätigen „${vorschau.firmenname}" eintippen`}
                  value={bestaetigung}
                  onChange={e => setBestaetigung(e.target.value)}
                  placeholder={vorschau.firmenname}
                  hinweis="Diese Eingabe verhindert versehentliches Löschen."
                  autoComplete="off"
                />

                <div className="flex gap-2">
                  <Knopf type="submit" variante="gefahr" laedt={sendet}
                         disabled={bestaetigung !== vorschau.firmenname || gewaehlt.length === 0}>
                    Endgültig entfernen
                  </Knopf>
                  <Knopf type="button" onClick={() => { setResetOffen(false); setBestaetigung(''); }}>
                    Abbrechen
                  </Knopf>
                </div>
              </form>
            )}

            <p className="text-xs text-slate-500 dark:text-slate-400">{vorschau.hinweis}</p>
          </div>
        </Karte>
      )}

      {/* Das Gegenstück zum Zurücksetzen — gleich darunter, wo man es sucht. */}
      {istAdmin && <Wiederherstellung />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Die Seite: das Persönliche für alle, der Betrieb nur für Administratoren.
// Vorher war die ganze Seite Administratoren vorbehalten — Mitarbeiter kamen
// weder an ihre Zwei-Faktor-Anmeldung noch an ihre angemeldeten Geräte.
// ---------------------------------------------------------------------------
export default function Einstellungen() {
  const { mandant, istAdmin } = useAuth();
  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Einstellungen</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{mandant?.name}</p>
      </div>
      <Konto />
      <Zweifaktor />
      <Geraete />
      <Privatsphaere />
      {istAdmin && (
        <>
          <h2 className="pt-4 text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Betrieb</h2>
          <Betrieb />
        </>
      )}
    </div>
  );
}

/** Das eigene Konto — mit Passwort ändern und dem Weg zum eigenen Profil. */
function Konto() {
  const { benutzer, mandant, istAdmin } = useAuth();
  const [formular, setFormular] = useState<{ alt: string; neu: string; wdh: string } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);

  async function passwortAendern(e: React.FormEvent) {
    e.preventDefault();
    if (!formular || !benutzer) return;
    setFehler(null); setMeldung(null);
    if (formular.neu.length < 8) { setFehler('Das neue Passwort braucht mindestens 8 Zeichen.'); return; }
    if (formular.neu !== formular.wdh) { setFehler('Die beiden neuen Passwörter stimmen nicht überein.'); return; }
    setSendet(true);
    try {
      const r = await api<{ abgemeldeteGeraete?: number }>(`/users/${benutzer.id}`, {
        methode: 'PUT', daten: { oldPassword: formular.alt, password: formular.neu },
      });
      setFormular(null);
      setMeldung(`Passwort geändert. Alle anderen Anmeldungen wurden beendet${r.abgemeldeteGeraete ? ` (${r.abgemeldeteGeraete} Gerät${r.abgemeldeteGeraete === 1 ? '' : 'e'})` : ''} — hier bleiben Sie angemeldet.`);
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : 'Das Passwort konnte nicht geändert werden.');
    } finally { setSendet(false); }
  }

  const zeilen: Array<[string, string]> = [
    ['Angemeldet als', benutzer?.name ?? ''],
    ['Benutzername', benutzer?.username ?? ''],
    ['E-Mail', benutzer?.email || '—'],
    ['Rolle', benutzer?.isOwner ? 'Inhaber' : benutzer?.role === 'admin' ? 'Administrator' : 'Mitarbeiter'],
    ['Unternehmen', mandant?.name ?? ''],
  ];

  return (
    <Karte>
      <KartenKopf titel="Konto"
                  aktion={!formular && (
                    <Knopf variante="still" onClick={() => { setFormular({ alt: '', neu: '', wdh: '' }); setMeldung(null); }}>
                      Passwort ändern
                    </Knopf>
                  )} />
      {fehler && <div className="px-5 pt-4"><Hinweis art="fehler">{fehler}</Hinweis></div>}
      <Erfolgsmeldung text={meldung} />
      {formular && (
        <form onSubmit={passwortAendern} className="space-y-3 border-b border-slate-200 p-5 dark:border-slate-800">
          <div className="grid gap-3 sm:grid-cols-3">
            <Feld label="Aktuelles Passwort" type="password" autoComplete="current-password" required autoFocus
                  value={formular.alt} onChange={e => setFormular(f => f && ({ ...f, alt: e.target.value }))} />
            <Feld label="Neues Passwort" type="password" autoComplete="new-password" required
                  value={formular.neu} onChange={e => setFormular(f => f && ({ ...f, neu: e.target.value }))} />
            <Feld label="Neues Passwort wiederholen" type="password" autoComplete="new-password" required
                  value={formular.wdh} onChange={e => setFormular(f => f && ({ ...f, wdh: e.target.value }))} />
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">Mindestens 8 Zeichen. Alle anderen Geräte und Browser werden dabei abgemeldet.</p>
          <div className="flex gap-2">
            <Knopf type="submit" variante="primaer" laedt={sendet}>Passwort ändern</Knopf>
            <Knopf type="button" onClick={() => { setFormular(null); setFehler(null); }}>Abbrechen</Knopf>
          </div>
        </form>
      )}
      <dl className="divide-y divide-slate-200 text-sm dark:divide-slate-800">
        {zeilen.map(([t, w]) => (
          <div key={t} className="flex justify-between gap-4 px-5 py-2.5">
            <dt className="text-slate-500 dark:text-slate-400">{t}</dt>
            <dd className="truncate text-slate-900 dark:text-slate-100">{w}</dd>
          </div>
        ))}
      </dl>
      <div className="border-t border-slate-200 p-4 text-sm text-slate-600 dark:border-slate-800 dark:text-slate-400">
        {benutzer && <Link to={`/team/${benutzer.id}`} className="font-medium text-marke-600 hover:underline dark:text-marke-400">Mein Profil</Link>}
        {istAdmin && (
          <>
            {' · '}Firmenangaben und Logo: <Link to="/firma" className="font-medium text-marke-600 hover:underline dark:text-marke-400">Firma</Link>
            {' · '}Tarif und Abrechnung: <Link to="/tarif" className="font-medium text-marke-600 hover:underline dark:text-marke-400">Tarif</Link>
          </>
        )}
      </div>
    </Karte>
  );
}
